import { prefs } from '../app/prefs';

/**
 * Procedural audio: layered synth effects with reverb and stereo positioning, an adaptive
 * generative score (explore / combat / boss) keyed per realm tier, and ambient beds.
 */
export interface SfxOpts {
  x?: number;
  y?: number;
  vol?: number;
  throttleMs?: number;
}

interface ToneOpts {
  freq: number;
  type?: OscillatorType;
  dur: number;
  vol: number;
  attack?: number;
  slide?: number;
  detune?: number;
  filter?: { type: BiquadFilterType; freq: number; q?: number; to?: number };
  at?: number;
  curve?: 'exp' | 'lin';
}

interface NoiseOpts {
  dur: number;
  vol: number;
  type?: BiquadFilterType;
  freq?: number;
  q?: number;
  to?: number;
  attack?: number;
  at?: number;
}

type Mode = 'menu' | 'realm' | 'duel' | null;

const SCALES: Record<number, { root: number; steps: number[] }> = {
  1: { root: 146.83, steps: [0, 2, 3, 5, 7, 8, 10] }, // D aeolian
  2: { root: 110.0, steps: [0, 1, 3, 5, 7, 8, 10] }, // A phrygian
  3: { root: 82.41, steps: [0, 1, 4, 5, 7, 8, 10] }, // E phrygian dominant
};

function degreeFreq(scale: { root: number; steps: number[] }, degree: number, octave = 0): number {
  const n = scale.steps.length;
  const oct = Math.floor(degree / n) + octave;
  const st = scale.steps[((degree % n) + n) % n];
  return scale.root * Math.pow(2, oct + st / 12);
}

class Engine {
  ctx: AudioContext | null = null;
  master!: GainNode;
  comp!: DynamicsCompressorNode;
  sfxBus!: GainNode;
  musicBus!: GainNode;
  ambBus!: GainNode;
  reverb!: ConvolverNode;
  reverbIn!: GainNode;
  noiseBuf!: AudioBuffer;
  listener = { x: 0, y: 0 };
  private last = new Map<string, number>();
  music: Music | null = null;
  ambient: Ambient | null = null;

  ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AC();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.comp = ctx.createDynamicsCompressor();
      this.comp.threshold.value = -14;
      this.comp.knee.value = 18;
      this.comp.ratio.value = 4;
      this.comp.attack.value = 0.004;
      this.comp.release.value = 0.2;
      this.master.connect(this.comp);
      this.comp.connect(ctx.destination);
      this.sfxBus = ctx.createGain();
      this.musicBus = ctx.createGain();
      this.ambBus = ctx.createGain();
      this.sfxBus.connect(this.master);
      this.musicBus.connect(this.master);
      this.ambBus.connect(this.master);
      // reverb
      this.reverb = ctx.createConvolver();
      this.reverb.buffer = this.impulse(2.4, 2.8);
      this.reverbIn = ctx.createGain();
      this.reverbIn.gain.value = 1;
      this.reverbIn.connect(this.reverb);
      this.reverb.connect(this.master);
      const sfxSend = ctx.createGain();
      sfxSend.gain.value = 0.22;
      this.sfxBus.connect(sfxSend);
      sfxSend.connect(this.reverbIn);
      const musSend = ctx.createGain();
      musSend.gain.value = 0.4;
      this.musicBus.connect(musSend);
      musSend.connect(this.reverbIn);
      // shared noise
      const len = ctx.sampleRate * 2;
      this.noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.applyVolumes();
    } catch (e) {
      console.warn('audio unavailable', e);
      return null;
    }
    return this.ctx;
  }

  private impulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      let lp = 0;
      for (let i = 0; i < len; i++) {
        const env = Math.pow(1 - i / len, decay);
        const n = (Math.random() * 2 - 1) * env;
        lp += (n - lp) * 0.35; // damp highs over time
        d[i] = lp * (0.6 + 0.4 * (1 - i / len));
      }
    }
    return buf;
  }

  applyVolumes(): void {
    if (!this.ctx) return;
    const p = prefs.value;
    this.master.gain.value = p.master;
    this.sfxBus.gain.value = p.sfx;
    this.musicBus.gain.setTargetAtTime(p.music * 0.7, this.ctx.currentTime, 0.05);
    this.ambBus.gain.value = p.music * 0.55;
  }

  unlock(): void {
    const c = this.ensure();
    if (c && c.state === 'suspended') void c.resume();
  }

  setListener(x: number, y: number): void {
    this.listener.x = x;
    this.listener.y = y;
  }

  /** Output node for a sound with distance attenuation and panning; null when inaudible. */
  out(vol: number, opts?: SfxOpts, bus?: AudioNode): GainNode | null {
    const ctx = this.ensure();
    if (!ctx) return null;
    let gain = vol * (opts?.vol ?? 1);
    let pan = 0;
    if (opts && opts.x !== undefined && opts.y !== undefined) {
      const dx = opts.x - this.listener.x;
      const dy = opts.y - this.listener.y;
      const d = Math.hypot(dx, dy);
      const att = 1 / (1 + Math.pow(d / 420, 2));
      if (att < 0.04) return null;
      gain *= att;
      pan = Math.max(-0.85, Math.min(0.85, dx / 640));
    }
    const g = ctx.createGain();
    g.gain.value = gain;
    const dest = bus ?? this.sfxBus;
    if (Math.abs(pan) > 0.02) {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      p.connect(dest);
    } else g.connect(dest);
    return g;
  }

  tone(out: AudioNode, o: ToneOpts): void {
    const ctx = this.ctx!;
    const t0 = o.at ?? ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(o.freq, t0);
    if (o.detune) osc.detune.value = o.detune;
    if (o.slide && o.slide !== 1) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.freq * o.slide), t0 + o.dur);
    const g = ctx.createGain();
    const a = o.attack ?? 0.004;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(o.vol, t0 + a);
    if (o.curve === 'lin') g.gain.linearRampToValueAtTime(0.0001, t0 + o.dur);
    else g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
    let node: AudioNode = osc;
    if (o.filter) {
      const f = ctx.createBiquadFilter();
      f.type = o.filter.type;
      f.frequency.setValueAtTime(o.filter.freq, t0);
      if (o.filter.to) f.frequency.exponentialRampToValueAtTime(Math.max(30, o.filter.to), t0 + o.dur);
      f.Q.value = o.filter.q ?? 0.9;
      osc.connect(f);
      node = f;
    }
    node.connect(g);
    g.connect(out);
    osc.start(t0);
    osc.stop(t0 + o.dur + 0.05);
  }

  noise(out: AudioNode, o: NoiseOpts): void {
    const ctx = this.ctx!;
    const t0 = o.at ?? ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    src.loopStart = Math.random() * 1.5;
    const f = ctx.createBiquadFilter();
    f.type = o.type ?? 'bandpass';
    f.frequency.setValueAtTime(o.freq ?? 1000, t0);
    if (o.to) f.frequency.exponentialRampToValueAtTime(Math.max(30, o.to), t0 + o.dur);
    f.Q.value = o.q ?? 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(o.vol, t0 + (o.attack ?? 0.004));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
    src.connect(f);
    f.connect(g);
    g.connect(out);
    src.start(t0);
    src.stop(t0 + o.dur + 0.05);
  }

  bell(out: AudioNode, freq: number, dur: number, vol: number, at?: number): void {
    const ctx = this.ctx!;
    const t0 = at ?? ctx.currentTime;
    const partials: [number, number, number][] = [
      [1, 1, 1],
      [2.0, 0.5, 0.7],
      [3.01, 0.28, 0.5],
      [4.16, 0.14, 0.35],
      [5.43, 0.08, 0.25],
    ];
    for (const [ratio, amp, len] of partials) {
      this.tone(out, { freq: freq * ratio, type: 'sine', dur: dur * len, vol: vol * amp, attack: 0.002, at: t0 });
    }
  }

  private throttled(kind: string, ms: number): boolean {
    const now = performance.now();
    const last = this.last.get(kind) ?? 0;
    if (now - last < ms) return true;
    this.last.set(kind, now);
    return false;
  }

  // ------------------------------------------------------------------ effects catalogue

  sfx(kind: string, opts: SfxOpts = {}): void {
    if (this.throttled(kind, opts.throttleMs ?? 45)) return;
    const ctx = this.ensure();
    if (!ctx || ctx.state !== 'running') return;
    const o = (vol: number) => this.out(vol, opts);
    const t = ctx.currentTime;
    switch (kind) {
      case 'shoot': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.08, vol: 0.18, freq: 2400, to: 1100 });
        this.tone(out, { freq: 900, type: 'square', dur: 0.05, vol: 0.04, slide: 0.35 });
        return;
      }
      case 'bow': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.05, vol: 0.22, type: 'highpass', freq: 2800 });
        this.tone(out, { freq: 1400, type: 'triangle', dur: 0.09, vol: 0.07, slide: 0.5 });
        this.tone(out, { freq: 2600, type: 'sine', dur: 0.16, vol: 0.025, slide: 0.7, at: t + 0.02 });
        return;
      }
      case 'bolt_void': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 240, type: 'sawtooth', dur: 0.26, vol: 0.07, slide: 0.45, filter: { type: 'lowpass', freq: 1200, to: 300 } });
        this.tone(out, { freq: 700, type: 'sine', dur: 0.2, vol: 0.05, slide: 0.5 });
        this.noise(out, { dur: 0.12, vol: 0.05, freq: 500 });
        return;
      }
      case 'fire_shot': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.3, vol: 0.22, type: 'lowpass', freq: 1400, to: 300 });
        for (let i = 0; i < 4; i++) this.noise(out, { dur: 0.03, vol: 0.07, freq: 3000 + Math.random() * 2000, q: 3, at: t + i * 0.035 + Math.random() * 0.02 });
        return;
      }
      case 'frost_shot': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 1800, type: 'sine', dur: 0.25, vol: 0.05 });
        this.tone(out, { freq: 2700, type: 'sine', dur: 0.2, vol: 0.035, detune: 8 });
        this.noise(out, { dur: 0.12, vol: 0.08, type: 'highpass', freq: 5000 });
        return;
      }
      case 'zap': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.05, vol: 0.24, freq: 4000, q: 1.5 });
        this.tone(out, { freq: 110, type: 'square', dur: 0.08, vol: 0.08 });
        this.tone(out, { freq: 3000, type: 'sawtooth', dur: 0.06, vol: 0.05, slide: 0.08 });
        return;
      }
      case 'spit': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.12, vol: 0.14, freq: 800, q: 1.2 });
        this.tone(out, { freq: 300, type: 'sine', dur: 0.12, vol: 0.07, slide: 0.5 });
        return;
      }
      case 'shard': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.06, vol: 0.11, type: 'highpass', freq: 4000 });
        this.tone(out, { freq: 2200, type: 'triangle', dur: 0.06, vol: 0.045, slide: 0.75 });
        return;
      }
      case 'swing':
      case 'swing_void': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.22, vol: 0.33, freq: 750, to: 280, q: 0.7, attack: 0.02 });
        this.tone(out, { freq: 170, type: 'sine', dur: 0.15, vol: 0.09, slide: 0.5 });
        if (kind === 'swing_void') this.tone(out, { freq: 330, type: 'sawtooth', dur: 0.25, vol: 0.05, slide: 0.5, filter: { type: 'lowpass', freq: 900 } });
        return;
      }
      case 'swing_fast': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.1, vol: 0.2, freq: 1900, to: 900, attack: 0.01 });
        return;
      }
      case 'swing_blunt': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.25, vol: 0.32, type: 'lowpass', freq: 650, to: 200, attack: 0.02 });
        this.tone(out, { freq: 110, type: 'sine', dur: 0.18, vol: 0.12, slide: 0.55 });
        return;
      }
      case 'swing_mob': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.18, vol: 0.2, freq: 520, to: 250, attack: 0.015 });
        return;
      }
      case 'swing_boss': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.36, vol: 0.4, type: 'lowpass', freq: 420, to: 120, attack: 0.03 });
        this.tone(out, { freq: 90, type: 'sine', dur: 0.3, vol: 0.2, slide: 0.5 });
        return;
      }
      case 'hit':
      case 'hit_crit': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 210, type: 'sine', dur: 0.1, vol: 0.2, slide: 0.35 });
        this.noise(out, { dur: 0.045, vol: 0.14, freq: 1800, q: 1.2 });
        this.tone(out, { freq: 800, type: 'square', dur: 0.02, vol: 0.04 });
        if (kind === 'hit_crit') {
          this.bell(out, 1600, 0.28, 0.1);
          this.noise(out, { dur: 0.08, vol: 0.1, type: 'highpass', freq: 6000 });
        }
        return;
      }
      case 'hit_fire': {
        const out = o(1);
        if (!out) return;
        for (let i = 0; i < 3; i++) this.noise(out, { dur: 0.03, vol: 0.08, freq: 2500 + Math.random() * 2500, q: 3, at: t + i * 0.04 });
        return;
      }
      case 'hit_frost': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 3200, type: 'sine', dur: 0.12, vol: 0.05 });
        this.tone(out, { freq: 4300, type: 'sine', dur: 0.08, vol: 0.035, at: t + 0.02 });
        return;
      }
      case 'hit_lightning': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.04, vol: 0.16, freq: 4500, q: 1.5 });
        this.tone(out, { freq: 120, type: 'square', dur: 0.06, vol: 0.06 });
        return;
      }
      case 'hit_void': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 180, type: 'sine', dur: 0.16, vol: 0.06, slide: 2.2 });
        return;
      }
      case 'hit_holy': {
        const out = o(1);
        if (!out) return;
        this.bell(out, 1250, 0.22, 0.07);
        return;
      }
      case 'hit_blight': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 320, type: 'sine', dur: 0.12, vol: 0.06, slide: 0.55 });
        this.noise(out, { dur: 0.1, vol: 0.08, type: 'lowpass', freq: 600 });
        return;
      }
      case 'hurt': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 150, type: 'sawtooth', dur: 0.3, vol: 0.28, slide: 0.35, filter: { type: 'lowpass', freq: 700, to: 200 } });
        this.noise(out, { dur: 0.16, vol: 0.24, type: 'lowpass', freq: 900 });
        this.music?.duck(0.6, 0.35);
        return;
      }
      case 'death': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 220, type: 'sawtooth', dur: 1.3, vol: 0.22, slide: 0.18, filter: { type: 'lowpass', freq: 1200, to: 120 } });
        this.noise(out, { dur: 0.9, vol: 0.22, type: 'lowpass', freq: 500, to: 80 });
        this.tone(out, { freq: 55, type: 'sine', dur: 0.2, vol: 0.3, at: t + 0.35 });
        this.tone(out, { freq: 55, type: 'sine', dur: 0.2, vol: 0.25, at: t + 0.75 });
        this.music?.duck(0.2, 1.8);
        return;
      }
      case 'mobdeath': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.2, vol: 0.15, freq: 650, to: 180 });
        this.tone(out, { freq: 260, type: 'triangle', dur: 0.16, vol: 0.07, slide: 0.35 });
        return;
      }
      case 'revdeath': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.4, vol: 0.18, freq: 900, to: 150 });
        this.tone(out, { freq: 330, type: 'sawtooth', dur: 0.6, vol: 0.08, slide: 0.25, filter: { type: 'lowpass', freq: 1000, to: 200 } });
        this.bell(out, 440, 0.8, 0.06);
        return;
      }
      case 'levelup': {
        const out = o(1);
        if (!out) return;
        const notes = [523.25, 659.25, 783.99, 1046.5];
        notes.forEach((f, i) => this.bell(out, f, 0.5, 0.12, t + i * 0.085));
        this.noise(out, { dur: 0.5, vol: 0.05, type: 'highpass', freq: 6000, attack: 0.1 });
        for (const f of [261.6, 329.6, 392]) this.tone(out, { freq: f, type: 'triangle', dur: 1.0, vol: 0.04, attack: 0.25, at: t + 0.1 });
        return;
      }
      case 'pick': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 880, type: 'square', dur: 0.05, vol: 0.045 });
        this.tone(out, { freq: 1320, type: 'square', dur: 0.06, vol: 0.035, at: t + 0.045 });
        return;
      }
      case 'pickup': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 1500, type: 'sine', dur: 0.09, vol: 0.07 });
        this.tone(out, { freq: 2250, type: 'sine', dur: 0.12, vol: 0.055, at: t + 0.05 });
        return;
      }
      case 'dash': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.2, vol: 0.2, freq: 900, to: 2600, attack: 0.02 });
        this.tone(out, { freq: 220, type: 'sine', dur: 0.12, vol: 0.035, slide: 2.4 });
        return;
      }
      case 'cast': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 330, type: 'sine', dur: 0.18, vol: 0.07, slide: 2 });
        this.noise(out, { dur: 0.12, vol: 0.05, freq: 1500 });
        return;
      }
      case 'cast_fire': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.45, vol: 0.28, type: 'lowpass', freq: 1800, to: 250, attack: 0.02 });
        for (let i = 0; i < 6; i++) this.noise(out, { dur: 0.03, vol: 0.08, freq: 2500 + Math.random() * 3000, q: 3, at: t + i * 0.05 + Math.random() * 0.03 });
        this.tone(out, { freq: 120, type: 'sine', dur: 0.3, vol: 0.12, slide: 0.5 });
        return;
      }
      case 'cast_frost': {
        const out = o(1);
        if (!out) return;
        for (const f of [1200, 1800, 2400]) this.tone(out, { freq: f, type: 'sine', dur: 0.45, vol: 0.045, attack: 0.02 });
        this.noise(out, { dur: 0.3, vol: 0.08, type: 'highpass', freq: 4000 });
        this.tone(out, { freq: 220, type: 'triangle', dur: 0.25, vol: 0.06, slide: 1.5 });
        return;
      }
      case 'cast_lightning': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.06, vol: 0.28, freq: 4200, q: 1.5 });
        this.noise(out, { dur: 0.05, vol: 0.2, freq: 3000, q: 1.5, at: t + 0.07 });
        this.tone(out, { freq: 60, type: 'square', dur: 0.22, vol: 0.09 });
        return;
      }
      case 'cast_void': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 80, type: 'sawtooth', dur: 0.45, vol: 0.1, slide: 2.6, attack: 0.08, filter: { type: 'lowpass', freq: 400, to: 1400 } });
        this.noise(out, { dur: 0.35, vol: 0.08, freq: 300, to: 900, attack: 0.12 });
        return;
      }
      case 'cast_holy': {
        const out = o(1);
        if (!out) return;
        for (const [f, d] of [
          [660, 0],
          [990, 0.03],
          [1320, 0.06],
        ])
          this.bell(out, f, 0.7, 0.07, t + d);
        this.noise(out, { dur: 0.4, vol: 0.04, type: 'highpass', freq: 6000, attack: 0.1 });
        return;
      }
      case 'cast_blight': {
        const out = o(1);
        if (!out) return;
        for (let i = 0; i < 3; i++) this.tone(out, { freq: 250 + i * 40, type: 'sine', dur: 0.1, vol: 0.06, slide: 0.7, at: t + i * 0.07 });
        this.noise(out, { dur: 0.3, vol: 0.09, type: 'lowpass', freq: 500 });
        return;
      }
      case 'cast_summon': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 110, type: 'sawtooth', dur: 0.55, vol: 0.11, attack: 0.05, filter: { type: 'lowpass', freq: 300, to: 900 } });
        this.noise(out, { dur: 0.4, vol: 0.09, freq: 600, to: 200 });
        this.bell(out, 440, 0.4, 0.05, t + 0.15);
        return;
      }
      case 'cast_curse': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 440, type: 'sawtooth', dur: 0.4, vol: 0.07, slide: 0.5, filter: { type: 'lowpass', freq: 800 } });
        this.noise(out, { dur: 0.25, vol: 0.06, freq: 900 });
        this.tone(out, { freq: 55, type: 'sine', dur: 0.4, vol: 0.08 });
        return;
      }
      case 'boss': {
        const out = o(1);
        if (!out) return;
        for (const f of [110, 165, 220]) this.tone(out, { freq: f, type: 'sawtooth', dur: 1.7, vol: 0.09, attack: 0.3, filter: { type: 'lowpass', freq: 700 } });
        for (const d of [0, 0.45]) {
          this.tone(out, { freq: 120, type: 'sine', dur: 0.45, vol: 0.32, slide: 0.3, at: t + d });
          this.noise(out, { dur: 0.3, vol: 0.28, type: 'lowpass', freq: 300, at: t + d });
        }
        this.music?.duck(0.4, 1.2);
        return;
      }
      case 'explosion': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.5, vol: 0.38, type: 'lowpass', freq: 700, to: 90 });
        this.tone(out, { freq: 90, type: 'sine', dur: 0.4, vol: 0.28, slide: 0.3 });
        for (let i = 0; i < 4; i++) this.noise(out, { dur: 0.03, vol: 0.06, freq: 2500 + Math.random() * 2500, q: 3, at: t + 0.05 + i * 0.05 });
        return;
      }
      case 'shield': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 300, type: 'triangle', dur: 0.25, vol: 0.07, slide: 2 });
        this.tone(out, { freq: 900, type: 'sine', dur: 0.3, vol: 0.035 });
        return;
      }
      case 'heal': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 660, type: 'sine', dur: 0.25, vol: 0.055, attack: 0.03 });
        this.tone(out, { freq: 880, type: 'sine', dur: 0.3, vol: 0.045, attack: 0.03, at: t + 0.06 });
        this.noise(out, { dur: 0.15, vol: 0.025, type: 'highpass', freq: 5000 });
        return;
      }
      case 'shrine': {
        const out = o(1);
        if (!out) return;
        this.bell(out, 880, 0.9, 0.09);
        this.bell(out, 1320, 0.9, 0.07, t + 0.08);
        this.noise(out, { dur: 0.6, vol: 0.04, type: 'highpass', freq: 6000, attack: 0.15 });
        return;
      }
      case 'chest': {
        const out = o(1);
        if (!out) return;
        this.noise(out, { dur: 0.05, vol: 0.2, type: 'lowpass', freq: 800 });
        this.tone(out, { freq: 200, type: 'square', dur: 0.05, vol: 0.05 });
        for (let i = 0; i < 3; i++) this.bell(out, 2000 + Math.random() * 1200, 0.18, 0.05, t + 0.1 + i * 0.06);
        return;
      }
      case 'altar': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 70, type: 'sine', dur: 0.7, vol: 0.2 });
        this.noise(out, { dur: 0.55, vol: 0.07, freq: 1200, q: 0.5, attack: 0.1 });
        this.tone(out, { freq: 233, type: 'sawtooth', dur: 0.5, vol: 0.04, slide: 0.8, filter: { type: 'lowpass', freq: 600 } });
        return;
      }
      case 'ui': {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 700, type: 'square', dur: 0.03, vol: 0.035 });
        return;
      }
      default: {
        const out = o(1);
        if (!out) return;
        this.tone(out, { freq: 400, type: 'square', dur: 0.05, vol: 0.04 });
      }
    }
  }

  // ------------------------------------------------------------------ music & ambient control

  musicMode(mode: Mode): void {
    const ctx = this.ensure();
    if (!ctx) return;
    if (!this.music) this.music = new Music(this);
    this.music.setMode(mode);
    if (!this.ambient) this.ambient = new Ambient(this);
    this.ambient.setActive(mode === 'realm' || mode === 'duel');
  }

  setIntensity(level: number): void {
    this.music?.setIntensity(level);
  }

  setTier(tier: number): void {
    this.music?.setTier(tier);
    this.ambient?.setTier(tier);
  }
}

// ---------------------------------------------------------------------- generative score

class Music {
  private mode: Mode = null;
  private tier = 1;
  private targetIntensity = 0;
  private intensity = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private nextStep = 0;
  private step = 0;
  private bar = 0;
  private bpm = 76;
  private drone: { a: OscillatorNode; b: OscillatorNode; f: BiquadFilterField; g: GainNode } | null = null;
  private pad: GainNode | null = null;
  private padVoices: { o1: OscillatorNode; o2: OscillatorNode; g: GainNode }[] = [];
  private lfo: OscillatorNode | null = null;
  private chordIdx = 0;
  private lastChordBar = -1;
  private motif: number[] = [];

  constructor(private e: Engine) {}

  setMode(mode: Mode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    if (!mode) {
      this.stop();
      return;
    }
    if (mode === 'menu') this.targetIntensity = 0;
    if (mode === 'duel') this.targetIntensity = 1;
    this.start();
  }

  setIntensity(level: number): void {
    if (this.mode === 'menu') return;
    this.targetIntensity = Math.max(0, Math.min(2, level));
  }

  setTier(tier: number): void {
    this.tier = tier;
  }

  duck(depth: number, seconds: number): void {
    const ctx = this.e.ctx;
    if (!ctx) return;
    const g = this.e.musicBus.gain;
    const base = prefs.value.music * 0.7;
    g.cancelScheduledValues(ctx.currentTime);
    g.setValueAtTime(base * depth, ctx.currentTime);
    g.linearRampToValueAtTime(base, ctx.currentTime + seconds);
  }

  private scale() {
    return SCALES[this.mode === 'menu' ? 1 : this.tier] ?? SCALES[1];
  }

  private start(): void {
    const ctx = this.e.ctx!;
    if (this.timer) return;
    this.buildDrone();
    this.buildPad();
    this.nextStep = ctx.currentTime + 0.1;
    this.step = 0;
    this.bar = 0;
    this.lastChordBar = -1;
    this.timer = setInterval(() => this.schedule(), 70);
  }

  private stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const ctx = this.e.ctx;
    const t = ctx ? ctx.currentTime : 0;
    if (this.drone) {
      this.drone.g.gain.setTargetAtTime(0.0001, t, 0.6);
      const d = this.drone;
      setTimeout(() => {
        d.a.stop();
        d.b.stop();
      }, 2500);
      this.drone = null;
    }
    for (const v of this.padVoices) {
      v.g.gain.setTargetAtTime(0.0001, t, 0.6);
      setTimeout(() => {
        v.o1.stop();
        v.o2.stop();
      }, 2500);
    }
    this.padVoices = [];
    this.pad = null;
    if (this.lfo) {
      this.lfo.stop(t + 3);
      this.lfo = null;
    }
  }

  private buildDrone(): void {
    const ctx = this.e.ctx!;
    const s = this.scale();
    const a = ctx.createOscillator();
    a.type = 'sawtooth';
    a.frequency.value = s.root / 2;
    const b = ctx.createOscillator();
    b.type = 'triangle';
    b.frequency.value = s.root / 2 * 1.5;
    b.detune.value = -6;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 220;
    f.Q.value = 2;
    const g = ctx.createGain();
    g.gain.value = 0.0001;
    g.gain.setTargetAtTime(0.07, ctx.currentTime, 1.5);
    a.connect(f);
    b.connect(f);
    f.connect(g);
    g.connect(this.e.musicBus);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lg = ctx.createGain();
    lg.gain.value = 110;
    lfo.connect(lg);
    lg.connect(f.frequency);
    lfo.start();
    a.start();
    b.start();
    this.lfo = lfo;
    this.drone = { a, b, f, g };
  }

  private buildPad(): void {
    const ctx = this.e.ctx!;
    this.pad = ctx.createGain();
    this.pad.gain.value = 1;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 900;
    f.Q.value = 0.7;
    this.pad.connect(f);
    f.connect(this.e.musicBus);
    for (let i = 0; i < 4; i++) {
      const o1 = ctx.createOscillator();
      o1.type = 'sawtooth';
      const o2 = ctx.createOscillator();
      o2.type = 'triangle';
      o2.detune.value = 7;
      const g = ctx.createGain();
      g.gain.value = 0.0001;
      o1.connect(g);
      o2.connect(g);
      g.connect(this.pad);
      o1.start();
      o2.start();
      this.padVoices.push({ o1, o2, g });
    }
  }

  private schedule(): void {
    const ctx = this.e.ctx;
    if (!ctx || !this.mode) return;
    // smooth intensity
    this.intensity += (this.targetIntensity - this.intensity) * 0.08;
    // background tabs throttle timers to ~1 Hz, so schedule further ahead there
    const lookahead = ctx.currentTime + (document.hidden ? 1.6 : 0.35);
    while (this.nextStep < lookahead) {
      this.playStep(this.nextStep);
      const targetBpm = this.mode === 'menu' ? 66 : this.intensity < 0.5 ? 76 : this.intensity < 1.5 ? 96 : 118;
      if (this.step % 16 === 0) this.bpm += (targetBpm - this.bpm) * 0.5;
      this.nextStep += 60 / this.bpm / 4;
      this.step++;
      if (this.step % 16 === 0) this.bar++;
    }
  }

  private chordDegrees(): number[] {
    // progressions by intensity; degrees within the scale (0 = tonic)
    if (this.mode === 'menu') return [[0, 2, 4], [5, 0, 2], [3, 5, 0], [6, 1, 3]][this.chordIdx % 4];
    if (this.intensity < 0.5) return [[0, 2, 4], [5, 0, 2], [6, 1, 3], [3, 5, 0]][this.chordIdx % 4];
    if (this.intensity < 1.5) return [[0, 2, 4], [1, 3, 5], [4, 6, 1], [0, 2, 4]][this.chordIdx % 4];
    return [[0, 2, 4], [1, 3, 5], [0, 2, 4], [5, 0, 2]][this.chordIdx % 4];
  }

  private playStep(t: number): void {
    const e = this.e;
    const s = this.scale();
    const inBar = this.step % 16;
    const I = this.intensity;
    const barsPerChord = I < 1.5 ? 2 : 1;
    // chord change
    if (inBar === 0 && Math.floor(this.bar / barsPerChord) !== this.lastChordBar) {
      this.lastChordBar = Math.floor(this.bar / barsPerChord);
      this.chordIdx++;
      const deg = this.chordDegrees();
      const padVol = 0.035 + I * 0.012;
      this.padVoices.forEach((v, i) => {
        const d = deg[i % deg.length] + (i === 3 ? 7 : 0);
        const f = degreeFreq(s, d, i === 3 ? 0 : 1) / 2;
        v.o1.frequency.setTargetAtTime(f, t, 0.05);
        v.o2.frequency.setTargetAtTime(f, t, 0.05);
        v.g.gain.cancelScheduledValues(t);
        v.g.gain.setValueAtTime(Math.max(0.0001, v.g.gain.value), t);
        v.g.gain.linearRampToValueAtTime(padVol, t + 1.4);
      });
      if (this.drone) {
        this.drone.a.frequency.setTargetAtTime(s.root / 2, t, 0.3);
        this.drone.b.frequency.setTargetAtTime((s.root / 2) * 1.5, t, 0.3);
      }
      // brass swell in boss intensity
      if (I >= 1.5 && this.bar % 2 === 0) {
        const out = e.out(1, undefined, e.musicBus);
        if (out) for (const [d, oct] of [[0, 0], [4, 0], [0, 1]] as [number, number][]) e.tone(out, { freq: degreeFreq(s, d, oct), type: 'sawtooth', dur: 2.6, vol: 0.05, attack: 0.7, filter: { type: 'lowpass', freq: 900 }, at: t, curve: 'lin' });
      }
      // regenerate the bell motif every 4 chords
      if (this.chordIdx % 4 === 1 || !this.motif.length) {
        const pent = [0, 2, 4, 5, 7];
        this.motif = Array.from({ length: 8 }, () => pent[Math.floor(Math.random() * pent.length)] + (Math.random() < 0.3 ? 7 : 0));
      }
    }
    const out = e.out(1, undefined, e.musicBus);
    if (!out) return;
    // bells: sparse in explore, denser in combat
    const bellChance = this.mode === 'menu' ? 0.28 : I < 0.5 ? 0.3 : I < 1.5 ? 0.45 : 0.55;
    if (inBar % 2 === 0 && Math.random() < bellChance) {
      const idx = (inBar / 2 + this.bar * 2) % this.motif.length;
      const deg = this.motif[idx] ?? 0;
      e.bell(out, degreeFreq(s, deg, 2), 1.2, 0.055 + I * 0.01, t);
    }
    // sub pulse & drums
    if (I >= 0.5) {
      const pulseVol = Math.min(1, (I - 0.4) * 1.6);
      if (inBar === 0 || inBar === 8 || (I >= 1.5 && inBar === 12)) {
        e.tone(out, { freq: s.root / 2, type: 'sine', dur: 0.32, vol: 0.14 * pulseVol, slide: 0.98, at: t });
        // kick
        e.tone(out, { freq: 150, type: 'sine', dur: 0.28, vol: 0.28 * pulseVol, slide: 0.27, at: t });
        e.noise(out, { dur: 0.06, vol: 0.12 * pulseVol, type: 'lowpass', freq: 400, at: t });
      }
      if (I >= 1.5) {
        if (inBar === 4 || inBar === 12) {
          e.noise(out, { dur: 0.14, vol: 0.16, freq: 1800, q: 0.9, at: t });
          e.tone(out, { freq: 180, type: 'triangle', dur: 0.12, vol: 0.09, slide: 0.6, at: t });
        }
        if (inBar % 2 === 1) e.noise(out, { dur: 0.03, vol: 0.05, type: 'highpass', freq: 7000, at: t });
        // taiko fill at the end of every 4th bar
        if (this.bar % 4 === 3 && inBar >= 12) {
          e.tone(out, { freq: 110, type: 'sine', dur: 0.2, vol: 0.22, slide: 0.4, at: t });
          e.noise(out, { dur: 0.08, vol: 0.12, type: 'lowpass', freq: 500, at: t });
        }
      } else if (I >= 0.5 && inBar === 6 && Math.random() < 0.5) {
        e.tone(out, { freq: 110, type: 'sine', dur: 0.18, vol: 0.12 * pulseVol, slide: 0.5, at: t });
      }
    }
    // occasional low choir-like swell in explore
    if (I < 0.5 && inBar === 0 && this.bar % 4 === 2) {
      const f = degreeFreq(s, 4, 0);
      e.tone(out, { freq: f, type: 'sawtooth', dur: 3.5, vol: 0.03, attack: 1.2, filter: { type: 'bandpass', freq: 600, q: 4 }, at: t, curve: 'lin' });
    }
  }
}

type BiquadFilterField = BiquadFilterNode;

// ---------------------------------------------------------------------- ambient beds

class Ambient {
  private active = false;
  private tier = 1;
  private wind: { src: AudioBufferSourceNode; f: BiquadFilterNode; g: GainNode; lfo: OscillatorNode } | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private e: Engine) {}

  setActive(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    if (on) this.start();
    else this.stop();
  }

  setTier(t: number): void {
    if (t === this.tier) return;
    this.tier = t;
    if (this.wind) {
      const ctx = this.e.ctx!;
      this.wind.f.frequency.setTargetAtTime(t === 3 ? 260 : t === 2 ? 420 : 620, ctx.currentTime, 2);
      this.wind.g.gain.setTargetAtTime(t === 3 ? 0.12 : 0.09, ctx.currentTime, 2);
    }
  }

  private start(): void {
    const ctx = this.e.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.e.noiseBuf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = 600;
    f.Q.value = 0.6;
    const g = ctx.createGain();
    g.gain.value = 0.0001;
    g.gain.setTargetAtTime(0.09, ctx.currentTime, 2);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lg = ctx.createGain();
    lg.gain.value = 180;
    lfo.connect(lg);
    lg.connect(f.frequency);
    const lfo2 = ctx.createOscillator();
    lfo2.frequency.value = 0.11;
    const lg2 = ctx.createGain();
    lg2.gain.value = 0.03;
    lfo2.connect(lg2);
    lg2.connect(g.gain);
    src.connect(f);
    f.connect(g);
    g.connect(this.e.ambBus);
    src.start();
    lfo.start();
    lfo2.start();
    this.wind = { src, f, g, lfo };
    this.scheduleOneShot();
  }

  private stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.wind) {
      const ctx = this.e.ctx!;
      this.wind.g.gain.setTargetAtTime(0.0001, ctx.currentTime, 1);
      const w = this.wind;
      setTimeout(() => {
        w.src.stop();
        w.lfo.stop();
      }, 3000);
      this.wind = null;
    }
  }

  private scheduleOneShot(): void {
    if (!this.active) return;
    const delay = 2500 + Math.random() * 6000;
    this.timer = setTimeout(() => {
      this.oneShot();
      this.scheduleOneShot();
    }, delay);
  }

  private oneShot(): void {
    const e = this.e;
    const ctx = e.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const out = e.out(1, { x: e.listener.x + (Math.random() - 0.5) * 900, y: e.listener.y + (Math.random() - 0.5) * 600 }, e.ambBus);
    if (!out) return;
    const t = ctx.currentTime;
    if (this.tier === 1) {
      // crickets: fast trills
      const f = 3200 + Math.random() * 900;
      for (let i = 0; i < 6; i++) e.tone(out, { freq: f, type: 'sine', dur: 0.05, vol: 0.05, at: t + i * 0.09 });
      if (Math.random() < 0.4) e.tone(out, { freq: 480, type: 'sine', dur: 0.5, vol: 0.03, slide: 0.8, at: t + 0.3, attack: 0.15 }); // owl-ish
    } else if (this.tier === 2) {
      // creaks and distant rattles
      e.tone(out, { freq: 180 + Math.random() * 120, type: 'sawtooth', dur: 0.7, vol: 0.035, slide: 0.6, filter: { type: 'lowpass', freq: 700 }, at: t, attack: 0.2 });
      for (let i = 0; i < 4; i++) e.noise(out, { dur: 0.04, vol: 0.05, freq: 1500 + Math.random() * 1500, q: 2, at: t + 0.8 + i * 0.09 });
    } else {
      // embers crackle and deep rumble
      for (let i = 0; i < 8; i++) e.noise(out, { dur: 0.03, vol: 0.06, freq: 2500 + Math.random() * 3000, q: 3, at: t + Math.random() * 1.2 });
      if (Math.random() < 0.5) e.tone(out, { freq: 45, type: 'sine', dur: 2.5, vol: 0.09, at: t, attack: 0.8, curve: 'lin' });
    }
  }
}

class AudioFacade {
  private e = new Engine();
  unlock(): void {
    this.e.unlock();
  }
  applyVolumes(): void {
    this.e.applyVolumes();
  }
  setListener(x: number, y: number): void {
    this.e.setListener(x, y);
  }
  sfx(kind: string, opts?: SfxOpts | number): void {
    this.e.sfx(kind, typeof opts === 'number' ? { throttleMs: opts } : opts);
  }
  music(mode: Mode): void {
    this.e.musicMode(mode);
  }
  setIntensity(level: number): void {
    this.e.setIntensity(level);
  }
  setTier(tier: number): void {
    this.e.setTier(tier);
  }
}

export const audio = new AudioFacade();
